import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Check, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import {
  useReviewSafewarePlanChange,
  useSafewarePlans,
  useSyncSafewarePlans,
  type SafewarePlan,
  type SafewarePlanChange,
} from '@/api/safeware'
import { LoadingButton } from '@/components/loading-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatEventTime, formatMoney } from '@/lib/utils'

type EquipmentType = 'single_appliance' | 'refrigerator' | 'ice_maker' | 'clothes_washer' | 'small_appliance'

const EQUIPMENT_TYPES: ReadonlyArray<{ value: EquipmentType; label: string }> = [
  { value: 'single_appliance', label: 'Standard equipment' },
  { value: 'refrigerator', label: 'Refrigerator' },
  { value: 'ice_maker', label: 'Ice maker' },
  { value: 'clothes_washer', label: 'Clothes washer' },
  { value: 'small_appliance', label: 'Small appliances' },
]

const VISIBLE_CHANGES = 8

function months(value: number): string {
  if (value === 0) return 'None'
  if (value % 12 === 0) return `${value / 12} yr`
  return `${value} mo`
}

function termLabel(value: number): string {
  if (value % 12 !== 0) return `${value} months`
  return value === 12 ? '1 year' : `${value / 12} years`
}

/** A band by its round edges: Safeware's $300–$599.99 reads "$300 – $600". */
function bandLabel(plan: SafewarePlan): string {
  const whole = (value: number) => formatMoney(value).replace(/\.00$/, '')
  const high = whole(Number(plan.retail_price_high) + 0.01)
  if (Number(plan.retail_price_low) <= 0.01) return `Under ${high}`
  return `${whole(Number(plan.retail_price_low))} – ${high}`
}

function errorDetail(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'detail' in error) {
    const detail = (error as { detail: unknown }).detail
    if (typeof detail === 'string') return detail
  }
  return fallback
}

function ChangeRow({ change }: { change: SafewarePlanChange }) {
  const review = useReviewSafewarePlanChange()
  const reviewed = !!change.reviewed_at
  return (
    <li className="flex items-start gap-3 px-4 py-2.5">
      <div className="min-w-0 flex-1">
        <p className={reviewed ? 'text-sm text-muted-foreground' : 'text-sm'}>{change.summary}</p>
        <p className="text-xs text-muted-foreground">
          {formatEventTime(change.created_at)}
          {change.reviewed_at && ` · Reviewed ${formatEventTime(change.reviewed_at)}`}
        </p>
      </div>
      {!reviewed && (
        <LoadingButton
          variant="outline"
          size="sm"
          className="shrink-0"
          loading={review.isPending && review.variables === change.id}
          onClick={() =>
            review.mutate(change.id, {
              onError: (error) => toast.error(errorDetail(error, 'Could not mark the change reviewed')),
            })
          }
        >
          <Check className="size-3.5" aria-hidden="true" />
          Reviewed
        </LoadingButton>
      )}
    </li>
  )
}

function PlanCell({ plan }: { plan: SafewarePlan | undefined }) {
  if (!plan) return <span className="text-muted-foreground">—</span>
  return (
    <Link
      to="/products/$productId"
      params={{ productId: plan.product_uuid }}
      className="group inline-flex flex-col items-end tabular-nums"
      title={plan.sku}
    >
      <span className="font-medium group-hover:text-primary group-hover:underline">{formatMoney(plan.price)}</span>
      <span className="text-xs text-muted-foreground">Net {formatMoney(plan.net)}</span>
    </Link>
  )
}

/** The Safeware manufacturer's synced plan catalog, its last sync and the changes ops reviews (USA-1161). */
export function SafewarePlansPanel() {
  const { data, isLoading } = useSafewarePlans()
  const sync = useSyncSafewarePlans()
  const [equipmentType, setEquipmentType] = useState<EquipmentType>('single_appliance')
  const [warranty, setWarranty] = useState<number>(12)
  const [showAllChanges, setShowAllChanges] = useState(false)

  const current = useMemo(() => (data?.plans ?? []).filter((plan) => !plan.discontinued_at), [data])
  const ofType = useMemo(() => current.filter((plan) => plan.equipment_type === equipmentType), [current, equipmentType])
  const warrantyOptions = useMemo(
    () => [...new Set(ofType.map((plan) => plan.manufacturer_warranty_months))].sort((a, b) => a - b),
    [ofType],
  )
  const selectedWarranty = warrantyOptions.includes(warranty)
    ? warranty
    : warrantyOptions.includes(12)
      ? 12
      : (warrantyOptions[0] ?? 12)
  const grid = useMemo(() => {
    const plans = ofType.filter((plan) => plan.manufacturer_warranty_months === selectedWarranty)
    const terms = [...new Set(plans.map((plan) => plan.term_months))].sort((a, b) => a - b)
    const rows = new Map<string, { label: string; low: number; limited: boolean; byTerm: Map<number, SafewarePlan> }>()
    for (const plan of plans) {
      const key = `${plan.limited_use}-${plan.retail_price_low}-${plan.retail_price_high}`
      const row = rows.get(key) ?? {
        label: plan.limited_use ? `Limited use, ${bandLabel(plan).toLowerCase()}` : bandLabel(plan),
        low: Number(plan.retail_price_low),
        limited: plan.limited_use,
        byTerm: new Map<number, SafewarePlan>(),
      }
      row.byTerm.set(plan.term_months, plan)
      rows.set(key, row)
    }
    return {
      terms,
      rows: [...rows.values()].sort((a, b) => Number(a.limited) - Number(b.limited) || a.low - b.low),
    }
  }, [ofType, selectedWarranty])

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  const lastSync = data?.last_sync
  const lastSuccess = data?.last_successful_sync
  const changes = data?.changes ?? []
  const openChanges = changes.filter((change) => !change.reviewed_at)
  const visibleChanges = showAllChanges ? changes : changes.slice(0, Math.max(VISIBLE_CHANGES, openChanges.length))
  const typeOffered = equipmentType !== 'small_appliance'

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <p className="text-sm text-muted-foreground">
          {lastSuccess ? (
            <>
              Synced {formatEventTime(lastSuccess.finished_at ?? lastSuccess.started_at)} ·{' '}
              <span className="tabular-nums">{current.length}</span> plans
            </>
          ) : (
            'Not synced yet'
          )}
        </p>
        <LoadingButton
          variant="outline"
          size="sm"
          className="ml-auto"
          loading={sync.isPending}
          loadingText="Syncing…"
          onClick={() =>
            sync.mutate(undefined, {
              onSuccess: (result) =>
                toast.success(
                  result.added || result.removed || result.repriced
                    ? `Synced: ${result.added} added, ${result.removed} removed, ${result.repriced} repriced`
                    : 'Synced: no changes',
                ),
              onError: (error) => toast.error(errorDetail(error, 'Could not reach Safeware')),
            })
          }
        >
          <RefreshCw className="size-3.5" aria-hidden="true" />
          Sync now
        </LoadingButton>
      </div>

      {lastSync && lastSync.status === 'failed' && (
        <p role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-2.5 text-sm text-destructive">
          Sync failed {formatEventTime(lastSync.started_at)}. Showing plans from the last good sync.
        </p>
      )}

      {changes.length > 0 && (
        <section aria-labelledby="safeware-changes" className="overflow-hidden rounded-xl border bg-card">
          <div className="flex items-center gap-2 border-b px-4 py-2">
            <h3 id="safeware-changes" className="text-sm font-semibold">
              Plan changes
            </h3>
            {openChanges.length > 0 && <Badge variant="secondary">{openChanges.length} to review</Badge>}
          </div>
          <ul className="divide-y">
            {visibleChanges.map((change) => (
              <ChangeRow key={change.id} change={change} />
            ))}
          </ul>
          {changes.length > visibleChanges.length && (
            <div className="border-t px-4 py-1.5">
              <Button variant="ghost" size="sm" onClick={() => setShowAllChanges(true)}>
                Show all {changes.length}
              </Button>
            </div>
          )}
        </section>
      )}

      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <ReviewChoiceGroup
          label="Equipment"
          value={equipmentType}
          options={EQUIPMENT_TYPES}
          onChange={setEquipmentType}
        />
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            Manufacturer warranty
          </span>
          <ReviewChoiceGroup
            label="Manufacturer warranty"
            value={String(selectedWarranty)}
            options={warrantyOptions.map((value) => ({ value: String(value), label: months(value) }))}
            onChange={(value) => setWarranty(Number(value))}
          />
        </div>
      </div>

      {grid.rows.length ? (
        <Table aria-describedby={typeOffered ? undefined : 'safeware-not-offered'}>
          <TableHeader>
            <TableRow>
              <TableHead>
                <span className="inline-flex items-center gap-2">
                  Product price
                  {!typeOffered && (
                    <Badge id="safeware-not-offered" variant="outline">
                      Not offered
                    </Badge>
                  )}
                </span>
              </TableHead>
              {grid.terms.map((term) => (
                <TableHead key={term} className="text-right">
                  <span className="block">{termLabel(term)}</span>
                  <span className="block text-xs font-normal text-muted-foreground">
                    +{months(term - selectedWarranty)}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {grid.rows.map((row) => (
              <TableRow key={row.label} className={row.limited || !typeOffered ? 'text-muted-foreground' : undefined}>
                <TableCell className="whitespace-nowrap tabular-nums">
                  <span className="inline-flex items-center gap-2">
                    {row.label}
                    {row.limited && typeOffered && <Badge variant="outline">Not offered</Badge>}
                  </span>
                </TableCell>
                {grid.terms.map((term) => (
                  <TableCell key={term} className="text-right">
                    <PlanCell plan={row.byTerm.get(term)} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <p className="py-12 text-center text-muted-foreground">
          {lastSuccess ? 'Safeware has no plans for this equipment' : 'Sync to load Safeware’s plans'}
        </p>
      )}
    </div>
  )
}
